import React from 'react';
import { Box, Button, Stack } from '@mui/material';
import { Phone, Email, Launch, CalendarMonth, ContactMail } from '@mui/icons-material';

export interface ActionButton {
  id: string;
  type: 'book' | 'call' | 'email' | 'link' | 'quote';
  label: string;
  style?: 'primary' | 'secondary' | 'outline';
  url?: string;
  phone?: string;
  email?: string;
  tourId?: string;
  analytics?: {
    event: string;
    category: string;
    label?: string;
  };
}

interface ActionButtonsProps {
  actions: ActionButton[];
  onButtonClick?: (action: ActionButton) => void;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({ actions, onButtonClick }) => {
  if (!actions || actions.length === 0) {
    return null;
  }

  const handleButtonClick = (action: ActionButton) => {
    // Handle different button types
    switch (action.type) {
      case 'book':
      case 'link':
        if (action.url) {
          window.open(action.url, '_blank', 'noopener,noreferrer');
        }
        break;
      case 'call':
        if (action.phone) {
          window.open(`tel:${action.phone}`, '_self');
        }
        break;
      case 'email':
        if (action.email) {
          window.open(`mailto:${action.email}`, '_self');
        }
        break;
      default:
        console.warn('Unknown action type:', action.type);
    }

    // Call custom handler if provided
    if (onButtonClick) {
      onButtonClick(action);
    }
  };

  const getButtonIcon = (type: ActionButton['type']) => {
    switch (type) {
      case 'book':
        return <CalendarMonth fontSize="small" />;
      case 'call':
        return <Phone fontSize="small" />;
      case 'email':
        return <Email fontSize="small" />;
      case 'link':
        return <Launch fontSize="small" />;
      case 'quote':
        return <ContactMail fontSize="small" />;
      default:
        return null;
    }
  };

  const getButtonVariant = (style?: string): 'text' | 'outlined' | 'contained' => {
    switch (style) {
      case 'primary':
        return 'contained';
      case 'secondary':
        return 'outlined';
      case 'outline':
        return 'outlined';
      default:
        return 'contained';
    }
  };

  const getButtonColor = (type: ActionButton['type'], style?: string): 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' => {
    if (type === 'book' || type === 'quote') {
      return style === 'secondary' ? 'secondary' : 'primary';
    }
    return style === 'secondary' ? 'secondary' : 'primary';
  };

  return (
    <Box sx={{ 
      mt: 1.5, 
      pt: 1.5, 
      borderTop: '1px solid rgba(0,0,0,0.12)',
      backgroundColor: 'rgba(25, 118, 210, 0.04)',
      borderRadius: 1,
      p: 1.5
    }}>
      <Stack 
        direction="row"
        spacing={1}
        sx={{ 
          width: '100%',
          flexWrap: 'wrap',
          gap: 1
        }}
      >
        {actions.map((action) => (
          <Button
            key={action.id}
            variant={getButtonVariant(action.style)}
            color={getButtonColor(action.type, action.style)}
            startIcon={getButtonIcon(action.type)}
            onClick={() => handleButtonClick(action)}
            size="small"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 2,
              py: 0.5,
              boxShadow: action.style === 'primary' ? 1 : 0,
              '&:hover': {
                boxShadow: action.style === 'primary' ? 3 : 1,
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            {action.label}
          </Button>
        ))}
      </Stack>
    </Box>
  );
};

export default ActionButtons; 